import React from 'react';
import { Button, Label, cn } from '@ideoz/ui';
import { Check, Loader2, RefreshCw, Sparkles, X } from 'lucide-react';
import ColorPicker from './ColorPicker';
import { DesignSystemSectionNav } from './DesignSystemSectionNav';
import { DesignSystemLivePreview } from './DesignSystemLivePreview';
import { UnsavedChangesDot } from './UnsavedChangesDot';
import { PillOptionGroup } from './PillOptionGroup';
import { PatternGroup } from './PatternGroup';
import { CornerRadiusSelector } from './CornerRadiusSelector';
import { FontFamilyPicker } from './FontFamilyPicker';
import type { DesignSystem, UxPatterns } from './DesignSystemPanel';
import {
  COLOR_PALETTES,
  DENSITY_OPTIONS,
  ELEVATION_PILLS,
  elevationToShadows,
  FONT_PAIRINGS,
  getActiveFontPairingId,
  getActivePaletteId,
  CONTAINER_WIDTH_OPTIONS,
  DELETE_PATTERN_OPTIONS,
  DETAIL_PATTERN_OPTIONS,
  EMPTY_STATE_PATTERN_OPTIONS,
  FILTER_PATTERN_OPTIONS,
  FORM_PATTERN_OPTIONS,
  LIST_PATTERN_OPTIONS,
  LOADING_PATTERN_OPTIONS,
  NAV_PATTERN_OPTIONS,
  SEARCH_PATTERN_OPTIONS,
  THEME_COLOR_PRESETS,
  THEME_OPTIONS,
  type DesignSystemSectionId,
} from '../designSystemConstants';
import { loadGoogleFontPreview } from '../googleFonts';

type DesignSystemPageLayoutProps = {
  designSystem: DesignSystem;
  setDesignSystem: React.Dispatch<React.SetStateAction<DesignSystem>>;
  previewUrl: string | null;
  isDirty: boolean;
  saving: boolean;
  isExtracting: boolean;
  extractFiles: File[];
  extractPreviews: string[];
  isDragOver: boolean;
  sectionRefs: Record<DesignSystemSectionId, React.RefObject<HTMLElement>>;
  centerScrollRef: React.RefObject<HTMLDivElement>;
  onSectionClick: (id: DesignSystemSectionId) => void;
  onExtractClick: () => void;
  onChooseScreenshots: () => void;
  onExtract: () => void;
  onRemoveExtractFile: (idx: number) => void;
  onLogoClick: () => void;
  onLogoRemove: (e?: React.MouseEvent) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onSave: () => void;
  onReset: () => void;
  hasUpdatedDesignSystem: boolean;
};

function SectionBlock({
  id,
  sectionRef,
  title,
  description,
  children,
}: {
  id: DesignSystemSectionId;
  sectionRef: React.RefObject<HTMLElement>;
  title: string;
  description?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      id={id}
      ref={sectionRef}
      className="scroll-mt-4 rounded-2xl border border-gray-200/70 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
    >
      <div className="mb-5">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {description && (
          <p className="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

export function DesignSystemPageLayout({
  designSystem,
  setDesignSystem,
  previewUrl,
  isDirty,
  saving,
  isExtracting,
  extractFiles,
  extractPreviews,
  isDragOver,
  sectionRefs,
  centerScrollRef,
  onSectionClick,
  onExtractClick,
  onChooseScreenshots,
  onExtract,
  onRemoveExtractFile,
  onLogoClick,
  onLogoRemove,
  onDragEnter,
  onDragLeave,
  onDrop,
  onSave,
  onReset,
  hasUpdatedDesignSystem,
}: DesignSystemPageLayoutProps): JSX.Element {
  const activePaletteId = getActivePaletteId(designSystem);
  const activeFontId = getActiveFontPairingId(designSystem);
  const ux = designSystem.uxPatterns ?? {};

  const setUx = <K extends keyof UxPatterns>(key: K, value: UxPatterns[K]) => {
    setDesignSystem((s) => ({
      ...s,
      uxPatterns: { ...(s.uxPatterns ?? {}), [key]: value },
    }));
  };

  const applyPalette = (palette: (typeof COLOR_PALETTES)[number]) => {
    setDesignSystem((s) => ({
      ...s,
      primaryColor: palette.primary,
      secondaryColor: palette.accent,
      primaryForeground: palette.primaryForeground,
      secondaryForeground: palette.secondaryForeground,
    }));
  };

  const applyFontPairing = (pairing: (typeof FONT_PAIRINGS)[number]) => {
    setDesignSystem((s) => ({
      ...s,
      fontFamily: pairing.body,
      headingFontFamily: pairing.heading,
    }));
  };

  const applyTheme = (theme: NonNullable<DesignSystem['theme']>) => {
    setDesignSystem((s) => ({
      ...s,
      theme,
      ...THEME_COLOR_PRESETS[theme],
    }));
  };

  return (
    <div className="flex h-full min-h-0 overflow-hidden">
      <aside className="hidden w-[220px] shrink-0 border-r border-gray-100 px-5 py-6 dark:border-gray-800 xl:block">
        <DesignSystemSectionNav onSectionClick={onSectionClick} onExtractClick={onExtractClick} />
      </aside>

      <div
        ref={centerScrollRef}
        className="flex min-h-0 min-w-0 flex-1 justify-center overflow-y-auto overscroll-contain bg-gray-50/60 dark:bg-gray-950/40"
      >
        <div className="h-full w-full max-w-2xl px-6 py-6">
          <header className="mb-6">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Design system
              </h2>
              {isDirty && <UnsavedChangesDot />}
            </div>
            <p className="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              Set brand colors, typography and interaction patterns once — every scope under this
              startup inherits them.
            </p>
          </header>

          <div className="space-y-4 pb-6">
            <SectionBlock
              id="brand"
              sectionRef={sectionRefs.brand}
              title="Brand"
              description="Start from your existing product, or upload a logo."
            >
              <div className="space-y-4">
                <div className="rounded-xl border border-primary-100/80 bg-primary-50/30 p-4 dark:border-primary-500/15 dark:bg-primary-500/5">
                  <div className="mb-1 flex items-center gap-2">
                    <Sparkles className="size-4 text-primary-600" />
                    <Label className="text-sm font-semibold text-primary-700 dark:text-primary-300 mb-0">
                      Extract from screenshots
                    </Label>
                  </div>
                  <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    Add a list view, an add-item flow and your main navigation for the best
                    detection. PNG / JPEG / WEBP · max 4 MB each.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onChooseScreenshots}
                      disabled={isExtracting}
                    >
                      {extractFiles.length === 0 ? 'Choose screenshots' : 'Add more'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={onExtract}
                      disabled={isExtracting || extractFiles.length === 0}
                    >
                      {isExtracting ? 'Extracting…' : 'Extract'}
                    </Button>
                    {extractFiles.length > 0 && (
                      <span className="text-xs text-gray-500">
                        {extractFiles.length}/6 selected
                      </span>
                    )}
                  </div>
                  {extractPreviews.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {extractPreviews.map((url, idx) => (
                        <div
                          key={url}
                          className="relative size-16 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700"
                        >
                          <img
                            src={url}
                            alt={`Screenshot ${idx + 1}`}
                            className="size-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => onRemoveExtractFile(idx)}
                            aria-label={`Remove screenshot ${idx + 1}`}
                            disabled={isExtracting}
                            className="absolute right-0.5 top-0.5 rounded-full bg-white/90 p-0.5 shadow hover:bg-white disabled:opacity-50"
                          >
                            <X className="size-3 text-gray-700" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Logo
                  </Label>
                  <div
                    onClick={onLogoClick}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    className={cn(
                      'mt-2 cursor-pointer rounded-xl border border-dashed transition-colors',
                      isDragOver
                        ? 'border-primary-400 bg-primary-50 dark:bg-primary-500/10'
                        : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/50'
                    )}
                  >
                    {previewUrl ? (
                      <div className="flex items-center gap-3 p-3">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white p-1.5 dark:border-gray-700">
                          <img
                            src={previewUrl}
                            alt="Logo preview"
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                            Logo uploaded
                          </p>
                          <p className="text-xs text-gray-500">Click to replace</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLogoRemove(e);
                          }}
                          aria-label="Remove logo"
                          className="shrink-0 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800"
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white text-gray-400 shadow-sm dark:bg-gray-800">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <path
                              d="M5.69547 27.6372L14.4909 18.8418C15.0189 18.3138 15.2829 18.0498 15.5873 17.9509C15.8551 17.8639 16.1436 17.8639 16.4114 17.9509C16.7158 18.0498 16.9798 18.3138 17.5078 18.8418L26.2446 27.5786M18.666 20L22.4909 16.1752C23.0189 15.6471 23.2829 15.3831 23.5873 15.2842C23.8551 15.1972 24.1436 15.1972 24.4114 15.2842C24.7158 15.3831 24.9798 15.6471 25.5078 16.1752L29.3327 20M13.3327 12C13.3327 13.4728 12.1388 14.6667 10.666 14.6667C9.19326 14.6667 7.99935 13.4728 7.99935 12C7.99935 10.5272 9.19326 9.33333 10.666 9.33333C12.1388 9.33333 13.3327 10.5272 13.3327 12ZM9.06602 28H22.9327C25.1729 28 26.293 28 27.1486 27.564C27.9013 27.1805 28.5132 26.5686 28.8967 25.816C29.3327 24.9603 29.3327 23.8402 29.3327 21.6V10.4C29.3327 8.15979 29.3327 7.03969 28.8967 6.18404C28.5132 5.43139 27.9013 4.81947 27.1486 4.43597C26.293 4 25.1729 4 22.9327 4H9.06602C6.82581 4 5.7057 4 4.85005 4.43597C4.09741 4.81947 3.48548 5.43139 3.10199 6.18404C2.66602 7.03969 2.66602 8.15979 2.66602 10.4V21.6C2.66602 23.8402 2.66602 24.9603 3.10199 25.816C3.48548 26.5686 4.09741 27.1805 4.85005 27.564C5.7057 28 6.82581 28 9.06602 28Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            Upload logo
                          </p>
                          <p className="text-xs text-gray-400">SVG, PNG or JPG · max 4 MB</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </SectionBlock>

            <SectionBlock
              id="colors"
              sectionRef={sectionRefs.colors}
              title="Colors"
              description="Pick a ready-made palette, then fine-tune the two colors that matter."
            >
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {COLOR_PALETTES.map((palette) => {
                  const selected = activePaletteId === palette.id;
                  return (
                    <button
                      key={palette.id}
                      type="button"
                      onClick={() => applyPalette(palette)}
                      className={cn(
                        'relative rounded-xl border p-3 text-left transition-colors',
                        selected
                          ? 'border-primary-500 ring-1 ring-primary-500/30'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                      )}
                    >
                      {selected && (
                        <span className="absolute right-2 top-2 flex size-5 items-center justify-center rounded-full bg-primary-600 text-white">
                          <Check className="size-3" />
                        </span>
                      )}
                      <div className="mb-2 flex gap-1">
                        {palette.swatches.map((color) => (
                          <div
                            key={color}
                            className="h-8 flex-1 first:rounded-l-md last:rounded-r-md"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {palette.name}
                      </p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ColorPicker
                  name="Primary"
                  label={designSystem.primaryColor ?? '#334155'}
                  value={designSystem.primaryColor ?? '#334155'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, primaryColor: v }))}
                />
                <ColorPicker
                  name="Primary foreground"
                  label={designSystem.primaryForeground ?? '#FFFFFF'}
                  value={designSystem.primaryForeground ?? '#FFFFFF'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, primaryForeground: v }))}
                />
                <ColorPicker
                  name="Accent"
                  label={designSystem.secondaryColor ?? '#64748B'}
                  value={designSystem.secondaryColor ?? '#64748B'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, secondaryColor: v }))}
                />
                <ColorPicker
                  name="Secondary foreground"
                  label={designSystem.secondaryForeground ?? '#FFFFFF'}
                  value={designSystem.secondaryForeground ?? '#FFFFFF'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, secondaryForeground: v }))}
                />
              </div>

              <div className="mt-8 border-t border-gray-100 pt-5 dark:border-gray-800">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Semantic colors
                </p>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  Used for status pills, alerts, and form validation.
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <ColorPicker
                    name="Success"
                    label={designSystem.successColor ?? '#22C55E'}
                    value={designSystem.successColor ?? '#22C55E'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, successColor: v }))}
                  />
                  <ColorPicker
                    name="Warning"
                    label={designSystem.warningColor ?? '#EAB308'}
                    value={designSystem.warningColor ?? '#EAB308'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, warningColor: v }))}
                  />
                  <ColorPicker
                    name="Error"
                    label={designSystem.errorColor ?? '#EF4444'}
                    value={designSystem.errorColor ?? '#EF4444'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, errorColor: v }))}
                  />
                  <ColorPicker
                    name="Info"
                    label={designSystem.infoColor ?? '#3B82F6'}
                    value={designSystem.infoColor ?? '#3B82F6'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, infoColor: v }))}
                  />
                </div>
              </div>

              <div className="mt-8 border-t border-gray-100 pt-5 dark:border-gray-800">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Neutral palette
                </p>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  Card surfaces, dividers, and muted text — what gives the app its layered feel.
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <ColorPicker
                    name="Surface"
                    label={designSystem.surfaceColor ?? '#F8FAFC'}
                    value={designSystem.surfaceColor ?? '#F8FAFC'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, surfaceColor: v }))}
                  />
                  <ColorPicker
                    name="Border"
                    label={designSystem.borderColor ?? '#E2E8F0'}
                    value={designSystem.borderColor ?? '#E2E8F0'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, borderColor: v }))}
                  />
                  <ColorPicker
                    name="Muted text"
                    label={designSystem.mutedTextColor ?? '#64748B'}
                    value={designSystem.mutedTextColor ?? '#64748B'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, mutedTextColor: v }))}
                  />
                </div>
              </div>
            </SectionBlock>

            <SectionBlock
              id="typography"
              sectionRef={sectionRefs.typography}
              title="Typography"
              description="Choose a font pairing for headings and body text."
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {FONT_PAIRINGS.map((pairing) => {
                  const selected = activeFontId === pairing.id;
                  return (
                    <button
                      key={pairing.id}
                      type="button"
                      onClick={() => applyFontPairing(pairing)}
                      onMouseEnter={() => {
                        loadGoogleFontPreview(pairing.body);
                        if (pairing.heading) loadGoogleFontPreview(pairing.heading);
                      }}
                      className={cn(
                        'rounded-xl border px-4 py-3 text-left transition-colors',
                        selected
                          ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-500/10'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                      )}
                    >
                      <p
                        className="text-base font-semibold text-gray-900 dark:text-gray-100"
                        style={{ fontFamily: pairing.heading ?? pairing.body }}
                      >
                        {pairing.label.split(' / ')[0]}
                      </p>
                      <p
                        className="mt-0.5 text-sm text-gray-500"
                        style={{ fontFamily: pairing.body }}
                      >
                        {pairing.heading ? `${pairing.heading} / ${pairing.body}` : pairing.body}
                      </p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-8 border-t border-gray-100 pt-5 dark:border-gray-800">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Font library
                </p>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  Search Google Fonts to set body and heading typefaces individually.
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FontFamilyPicker
                    label="Body font"
                    value={designSystem.fontFamily}
                    onChange={(font) =>
                      setDesignSystem((s) => ({ ...s, fontFamily: font ?? s.fontFamily }))
                    }
                  />
                  <FontFamilyPicker
                    label="Heading font"
                    value={designSystem.headingFontFamily}
                    onChange={(font) => setDesignSystem((s) => ({ ...s, headingFontFamily: font }))}
                    sameAsBodyLabel="Same as body"
                  />
                </div>
              </div>
            </SectionBlock>

            <SectionBlock
              id="visual-style"
              sectionRef={sectionRefs['visual-style']}
              title="Visual style"
            >
              <div className="space-y-5">
                <div>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    Prototype theme
                  </p>
                  <PillOptionGroup
                    options={THEME_OPTIONS}
                    value={designSystem.theme ?? 'light'}
                    onChange={applyTheme}
                  />
                  <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    Sets the default background and surface colors for generated prototypes and the
                    live preview.
                  </p>
                </div>
                <div>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    App background
                  </p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <ColorPicker
                      name="App background"
                      label={designSystem.appBg ?? '#FFFFFF'}
                      value={designSystem.appBg ?? '#FFFFFF'}
                      onChange={(v) => setDesignSystem((s) => ({ ...s, appBg: v }))}
                    />
                  </div>
                </div>
                <CornerRadiusSelector
                  value={designSystem.borderRadius}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, borderRadius: v }))}
                />
                <div>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    Density
                  </p>
                  <PillOptionGroup
                    options={DENSITY_OPTIONS}
                    value={designSystem.density}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, density: v }))}
                  />
                </div>
                <div>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    Elevation
                  </p>
                  <PillOptionGroup
                    options={ELEVATION_PILLS}
                    value={designSystem.elevationStyle}
                    onChange={(v) =>
                      setDesignSystem((s) => ({
                        ...s,
                        elevationStyle: v,
                        shadows: elevationToShadows(v),
                      }))
                    }
                  />
                </div>
              </div>
            </SectionBlock>

            <SectionBlock
              id="patterns"
              sectionRef={sectionRefs.patterns}
              title="Interaction patterns"
              description="How your app behaves — used so the generated prototype matches your app's UX."
            >
              <div className="space-y-4">
                <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 dark:border-gray-800 dark:bg-gray-900/20">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Creating & editing
                  </p>
                  <div className="space-y-2">
                    <PatternGroup
                      label="Add new item"
                      options={FORM_PATTERN_OPTIONS}
                      value={ux.addPattern}
                      onChange={(v) => setUx('addPattern', v)}
                    />
                    <PatternGroup
                      label="Edit item"
                      options={FORM_PATTERN_OPTIONS}
                      value={ux.editPattern}
                      onChange={(v) => setUx('editPattern', v)}
                    />
                    <PatternGroup
                      label="Delete confirmation"
                      options={DELETE_PATTERN_OPTIONS}
                      value={ux.deletePattern}
                      onChange={(v) => setUx('deletePattern', v)}
                    />
                  </div>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 dark:border-gray-800 dark:bg-gray-900/20">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Browsing & finding
                  </p>
                  <div className="space-y-2">
                    <PatternGroup
                      label="List display"
                      options={LIST_PATTERN_OPTIONS}
                      value={ux.listPattern}
                      onChange={(v) => setUx('listPattern', v)}
                    />
                    <PatternGroup
                      label="Search"
                      options={SEARCH_PATTERN_OPTIONS}
                      value={ux.searchPattern}
                      onChange={(v) => setUx('searchPattern', v)}
                    />
                    <PatternGroup
                      label="Filters"
                      options={FILTER_PATTERN_OPTIONS}
                      value={ux.filterPattern}
                      onChange={(v) => setUx('filterPattern', v)}
                    />
                    <PatternGroup
                      label="Detail view"
                      options={DETAIL_PATTERN_OPTIONS}
                      value={ux.detailPattern}
                      onChange={(v) => setUx('detailPattern', v)}
                    />
                  </div>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 dark:border-gray-800 dark:bg-gray-900/20">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Shell & states
                  </p>
                  <div className="space-y-2">
                    <PatternGroup
                      label="Navigation"
                      options={NAV_PATTERN_OPTIONS}
                      value={ux.navPattern}
                      onChange={(v) => setUx('navPattern', v)}
                    />
                    <PatternGroup
                      label="Container width"
                      options={CONTAINER_WIDTH_OPTIONS}
                      value={ux.containerWidth}
                      onChange={(v) => setUx('containerWidth', v)}
                    />
                    <PatternGroup
                      label="Loading state"
                      options={LOADING_PATTERN_OPTIONS}
                      value={ux.loadingPattern}
                      onChange={(v) => setUx('loadingPattern', v)}
                    />
                    <PatternGroup
                      label="Empty state"
                      options={EMPTY_STATE_PATTERN_OPTIONS}
                      value={ux.emptyStatePattern}
                      onChange={(v) => setUx('emptyStatePattern', v)}
                    />
                  </div>
                </div>
              </div>
            </SectionBlock>
          </div>

          <div className="space-y-2 border-t border-gray-200 pt-4 xl:hidden dark:border-gray-800">
            <Button
              variant="primary"
              size="default"
              onClick={onSave}
              disabled={saving}
              className="w-full gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  {isDirty && <UnsavedChangesDot size="sm" />}
                  <RefreshCw className="size-4" />
                  Update design system
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
              disabled={saving}
              className="w-full"
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      <aside className="hidden w-[340px] shrink-0 flex-col border-l border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900 xl:flex">
        <DesignSystemLivePreview
          designSystem={designSystem}
          logoUrl={previewUrl}
          className="min-h-0 flex-1 rounded-none border-0"
        />
        <div className="shrink-0 space-y-2 border-t border-gray-100 p-4 dark:border-gray-800">
          <Button
            variant="primary"
            size="default"
            onClick={onSave}
            disabled={saving}
            className="w-full gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                {isDirty && <UnsavedChangesDot size="sm" />}
                <RefreshCw className="size-4" />
                Update design system
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onReset}
            disabled={saving}
            className="w-full"
          >
            Reset
          </Button>
        </div>
      </aside>
    </div>
  );
}

export default DesignSystemPageLayout;
